import { CheckCircleIcon } from "@heroicons/react/24/solid";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  IonButton,
  IonCol,
  IonGrid,
  IonItem,
  IonList,
  IonRouterLink,
  IonRow,
  IonText,
} from "@ionic/react";
import { useContext, useState } from "react";
import { SubmitHandler, useForm, type Control } from "react-hook-form";
import { z } from "zod";

import { $api, ApiMutateShape } from "@/api/api";
import { paths } from "@/api/v1";
import { ControlledInput } from "@/components/inputs/ControlledInput";
import { ApiTokenContext } from "@/contexts/api-token/ApiContextWrapper";
import { usePostAuthRedirect } from "@/hooks/usePostAuthRedirect";
import {
  LogRegHeader,
  LogRegPageLayout,
} from "@/pages/login/components/LoginRegisterLayout";
import { getLoginErrorMessage } from "@/utils/apiErrors";
import { BASE_PATH } from "@/utils/constants/api";
import { ROUTING } from "@/utils/constants/routing";
import { SHORT_TIMED_DELAY } from "@/utils/constants/time";
import { requiredTrimmedString } from "@/utils/form/validation";

import "./LogReg.css";

type Inputs =
  paths["/login"]["post"]["requestBody"]["content"]["application/json"];

const LoginFormData = z.object({
  password: requiredTrimmedString("Password is required"),
  userName: requiredTrimmedString("Username is required"),
});

type LoginFormDataType = z.infer<typeof LoginFormData>;

function onSubmit({
  mutate,
  mutationReset,
  login,
  setLoginError,
}: Pick<ApiMutateShape<"post", "/login">, "mutate"> & {
  login: (bearer: string, lTBearer: string) => void;
  mutationReset: () => void;
  setLoginError: (message: string | null) => void;
}): SubmitHandler<Inputs> {
  return (data) => {
    setLoginError(null);
    mutate(
      { baseUrl: BASE_PATH, body: { ...data } },
      {
        onError(error, _variables, _context) {
          setLoginError(getLoginErrorMessage(error));
        },
        onSettled() {
          setTimeout(mutationReset, SHORT_TIMED_DELAY);
        },
        onSuccess(res, _variables, _context) {
          login(res.bearer, res.lTToken);
        },
      }
    );
  };
}

function buildForgotPasswordLink(userName: string | undefined): string {
  const trimmed = userName?.trim();
  if (!trimmed) {
    return ROUTING.FORGOT_PASSWORD;
  }
  return `${ROUTING.FORGOT_PASSWORD}?userName=${encodeURIComponent(trimmed)}`;
}

function ButtonRow({
  isIdle,
  userName,
}: {
  isIdle: boolean;
  userName: string | undefined;
}) {
  const { token } = useContext(ApiTokenContext),
    forgotPasswordLink = buildForgotPasswordLink(userName);
  return (
    <>
      <IonRow>
        <IonCol>
          <IonButton
            disabled={!isIdle}
            hidden={Boolean(token)}
            type="submit"
            className="grow primary"
          >
            Login
          </IonButton>
          <IonButton
            hidden={Boolean(token) !== true}
            className="grow primary"
            routerLink="/dashboard"
          >
            Dashboard
          </IonButton>
        </IonCol>
      </IonRow>
      <IonRow hidden={Boolean(token)} className="ion-justify-content-center">
        <IonCol className="shrink">
          <IonRouterLink routerLink={forgotPasswordLink}>
            Forgot Password
          </IonRouterLink>
        </IonCol>
      </IonRow>
    </>
  );
}
function LoginHeader() {
  return (
    <LogRegHeader
      title="Login"
      subHeader={
        <IonText>
          Don&apos;t have an account? <br></br>
          <IonRouterLink routerLink="/register">
            You can register for free!
          </IonRouterLink>
        </IonText>
      }
    />
  );
}
function LoginInput({ control }: { control: Control<LoginFormDataType> }) {
  const { token } = useContext(ApiTokenContext);
  return (
    <IonList hidden={Boolean(token)} lines="none" className="content-block">
      <IonItem>
        <ControlledInput
          name="userName"
          control={control}
          label="Username"
          labelPlacement="floating"
          helperText=" "
          required
        />
      </IonItem>
      <IonItem className="content-sub-block">
        <ControlledInput
          name="password"
          control={control}
          label="Password"
          labelPlacement="floating"
          helperText=" "
          type="password"
          required
        />
      </IonItem>
    </IonList>
  );
}
function LoginPage() {
  const { login, token } = useContext(ApiTokenContext),
    { mutate, isIdle, reset: mutationReset } = $api.useMutation(
      "post",
      "/login"
    ),
    { control, handleSubmit, watch } = useForm<LoginFormDataType>({
      defaultValues: { password: "", userName: "" },
      mode: "onSubmit",
      resolver: zodResolver(LoginFormData),
    }),
    userName = watch("userName"),
    [loginError, setLoginError] = useState<string | null>(null),
    submit = onSubmit({ login, mutate, mutationReset, setLoginError });

  usePostAuthRedirect(Boolean(token));

  return (
    <LogRegPageLayout>
      <IonGrid>
        <form onSubmit={handleSubmit(submit)} noValidate>
          <LoginHeader />
          <IonRow
            hidden={Boolean(token) === false}
            class="ion-justify-content-center"
          >
            <CheckCircleIcon color="green" width={50} />
          </IonRow>
          <LoginInput control={control} />
          {loginError ? (
            <IonRow className="ion-justify-content-center content-block">
              <IonCol>
                <IonText color="danger">{loginError}</IonText>
              </IonCol>
            </IonRow>
          ) : null}
          <ButtonRow isIdle={isIdle} userName={userName} />
        </form>
      </IonGrid>
    </LogRegPageLayout>
  );
}

export { LoginPage };
